import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, createSearchParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { trackRep1Completed } from '../../utils/analytics';
import { formatGuestDueLabel } from '../../utils/demoState';
import {
  buildStudySessionSummaryPayload,
  mergeRoleExpUpdatesIntoSession,
  shouldShowRegisteredSessionSummary
} from '../../utils/studySessionSummary';
import {
  applySessionCardPenalty,
  applySessionCardSuccess,
  areAllCookbookProgressDotsGreen,
  countAttemptedSessionCards,
  countCleanSessionCards,
  formatCookbookIndexReviewLabel,
  isCookbookSnippetDueToday,
  isCookbookSnippetStarted,
  isCookbookSessionComplete,
  shouldTransitionToPracticeAfterSessionComplete,
  toLegacySessionResults
} from '../../utils/studySessionDotState';
import { computeRolePathOverview } from '../../utils/rolePathResume';
import useDailyProgress from '../../hooks/useDailyProgress';
import { useUserProfile, selectPrimaryRoleId } from '../../hooks/useUserProfile';
import usePublishedRoles from '../../hooks/usePublishedRoles';
import { formatSrsScheduleLabel, resolveSrsNextReviewDate } from '../SRS/algorithm';
import { isFirstExposurePending, isSrsPracticeOnlyForStudy } from './logic/studySrsWiring';
import {
  findNextEarliestReviewCookbookCardIndex,
  findNextIndexCookbookCardIndex,
  findNextUnstartedCookbookCardIndex,
  findRandomPracticeCookbookCardIndex
} from '../../utils/cookbookCardProgress';
import useStudyLogic from './logic/StudyLogic';
import { extractVariableHints } from '../../utils/codeProcessing';
import IconRenderer from '../ui/IconRenderer';
import { useDirectPremiumCheck } from '../../hooks/useDirectPremiumCheck';
import StudyModeCodeEditor from './StudyModeCodeEditor';
import StudyShell from './Shell/StudyShell';
import CardHeaderActions from './CardChrome/CardHeaderActions';
import ByteCompanion from './ByteCompanion/ByteCompanion';
import ByteUpgradeStrip from './ByteCompanion/ByteUpgradeStrip';
import StudyRoadmapContextPanel from './StudyRoadmapContextPanel';
import {
  isRoadmapUserCookbook,
  loadTrackNodes,
  loadTrackProgress,
  loadSnippetsByIds,
  ensureRoadmapCookbookForNode,
  loadPublishedTracks,
  loadPublishedLearningPaths,
  loadLearningPathEntries
} from '../../services/roadmapService';
import { fetchTrackMeta } from '../../services/achievementsService';
import {
  ROADMAP_GRAPH_LANG_SEARCH_PARAM,
  ROADMAP_GRAPH_SELECTED_TRACK_ID_STATE_KEY,
  ROADMAP_PATHS
} from '../Roadmap/roadmapPaths';
import { buildOrderedNodeGraph } from '../../utils/roadmapGraph';
import {
  formatLanguageDisplay,
  formatRoadmapTrackTitle,
  formatTrackTitle,
  normalizeRoadmapLanguageKey,
  trackMatchesLanguage
} from '../../utils/roadmapDisplay';
import { userCanAccessRoadmapTrack } from '../../utils/roadmapSubscription';
import {
  orderTracksForLanguageRoadmap,
  pickLearningPathForLanguage
} from '../../hooks/useLanguageTrackOrderGraph';
import { ReactComponent as CookbookIndexIcon } from '../../Icons/book.svg';
import StudyLoadingSkeleton from './StudyLoadingSkeleton';
import StudyExperiencePopup from './StudyExperiencePopup';
import './Study.css';
import './CardChrome/CardChrome.css';

const STUDY_DESCRIPTION_PAGE_CHARS = 56;

/** Split long card descriptions into header-sized pages (word-aware). */
function splitDescriptionPages(text, maxChars = STUDY_DESCRIPTION_PAGE_CHARS) {
  const normalized = (text || '').trim();
  if (!normalized) return [];
  if (normalized.length <= maxChars) return [normalized];

  const pages = [];
  let remaining = normalized;
  while (remaining.length > 0) {
    if (remaining.length <= maxChars) {
      pages.push(remaining);
      break;
    }
    let cut = remaining.lastIndexOf(' ', maxChars);
    if (cut <= 0) cut = maxChars;
    pages.push(remaining.slice(0, cut).trim());
    remaining = remaining.slice(cut).trim();
  }
  return pages;
}

/**
 * Study — Unified Study Mode (redesigned).
 *
 * Layout matches the supplied reference mockups
 * (flashcode_study_byte.html / flashcode_study_no_byte.html):
 *
 *   ┌────────── StudyShell ──────────┐
 *   │ navbar (brand · breadcrumb · streak · XP) │
 *   │ progress strip (bar + dots)              │
 *   │ ┌──────────── card body ────────────┐    │
 *   │ │ card header (lang · title · actions) │
 *   │ │ split body: code | Byte (Learn only) │
 *   │ └──────────────────────────────────┘    │
 *   │ footer (← Prev · counter+chips · Next →) │
 *   └────────────────────────────────────────┘
 *
 * Test Mode = code only, full width. Learn Mode = code | Byte coach.
 */
const Study = ({
  userId,
  deck,
  cards: initialCards,
  setView,
  previousView,
  onExitStudy,
  onReviewQueueChanged,
  onNodeCompleted,
  /** Guest demo: opens save modal. Registered: session tab in experience modal. */
  onSessionComplete,
  /** Registered users: session tab in experience modal. */
  showSessionSummary = false,
  onNavigateRolePath,
  /** App header back delegates here when study is active. */
  onRegisterStudyExitHandler,
  title,
  disableAiFeatures = false,
  /** Guest demo: opens save modal instead of subscription when Byte is gated. */
  onGuestUpgrade,
  /** Restart study with new options (e.g. roadmap study ↔ test mode toggle). */
  onStartStudying
}) => {
  // ── Deck loading (unchanged from previous Study.js) ──────────────────
  const [fullDeck, setFullDeck] = useState(null);
  const [startCardId, setStartCardId] = useState(null);
  const [roadmapTrackMeta, setRoadmapTrackMeta] = useState(null);
  const [roadmapTrackMetaLoading, setRoadmapTrackMetaLoading] = useState(false);
  const [roadmapTrackProgress, setRoadmapTrackProgress] = useState(null);
  const [roadmapTrackNodes, setRoadmapTrackNodes] = useState([]);
  const [roadmapNodeNavLoading, setRoadmapNodeNavLoading] = useState(false);
  const [roadmapLanguageTracks, setRoadmapLanguageTracks] = useState([]);
  const [roadmapLanguageTracksLoading, setRoadmapLanguageTracksLoading] = useState(false);
  const navigate = useNavigate();
  const { isPremiumUser } = useDirectPremiumCheck(userId);
  const { completedToday, masteredToday, displayStreak, streakAtRisk } = useDailyProgress(userId, {
    enabled: showSessionSummary
  });
  const { data: userProfileData } = useUserProfile(showSessionSummary ? userId : null);
  const primaryRoleId = selectPrimaryRoleId(userProfileData?.userData);
  const { roles: publishedRoles } = usePublishedRoles();
  const primaryRoleMeta = useMemo(
    () => publishedRoles.find((role) => role.roleId === primaryRoleId) || null,
    [publishedRoles, primaryRoleId]
  );
  const [experiencePanel, setExperiencePanel] = useState('rep');
  const [rolePathResume, setRolePathResume] = useState(null);

  const handleSubscriptionRequired = useCallback(() => {
    window.dispatchEvent(new CustomEvent('navigateToSubscription'));
  }, []);

  useEffect(() => {
    const fetchFullDeck = async () => {
      if (typeof deck === 'string' && userId) {
        try {
          const deckRef = doc(db, `users/${userId}/decks/${deck}`);
          const deckSnap = await getDoc(deckRef);
          if (deckSnap.exists()) {
            const deckData = { id: deckSnap.id, ...deckSnap.data() };
            setFullDeck(deckData);
            setStartCardId(null);
          }
        } catch (error) {
          console.error('Error fetching deck:', error);
          setStartCardId(null);
        }
      } else if (deck && typeof deck === 'object') {
        if (deck.practiceStartedSession) {
          if (deck.deckId && userId) {
            try {
              const deckRef = doc(db, `users/${userId}/decks/${deck.deckId}`);
              const deckSnap = await getDoc(deckRef);
              const deckData = deckSnap.exists()
                ? { id: deckSnap.id, ...deckSnap.data() }
                : { id: deck.deckId, type: 'cookbook' };
              setFullDeck({
                ...deckData,
                practiceStartedSession: true,
                deckId: deck.deckId,
                trackId: deck.trackId ?? deckData.roadmapTrackId ?? null,
                nodeId: deck.nodeId ?? deckData.roadmapNodeId ?? null
              });
            } catch (error) {
              console.error('Error fetching practice cookbook deck:', error);
              setFullDeck({
                id: deck.deckId,
                type: 'cookbook',
                title: 'Practice started',
                practiceStartedSession: true,
                deckId: deck.deckId
              });
            }
          } else {
            setFullDeck({
              id: `practice-started-${deck.trackId || 'track'}`,
              type: 'cookbook',
              title: 'Practice started',
              practiceStartedSession: true,
              roadmapTrackId: deck.trackId ?? null,
              trackId: deck.trackId ?? null,
              roadmapNodeId: deck.nodeId ?? null,
              nodeId: deck.nodeId ?? null
            });
          }
          setStartCardId(deck.startCardId ?? null);
        } else if (deck.deckId && deck.dueOnly) {
          try {
            const deckRef = doc(db, `users/${userId}/decks/${deck.deckId}`);
            const deckSnap = await getDoc(deckRef);
            if (deckSnap.exists()) {
              setFullDeck({ id: deckSnap.id, ...deckSnap.data() });
            }
            // Keep startCardId so a clicked track snippet opens first when it is due
            setStartCardId(deck.startCardId ?? null);
          } catch (error) {
            console.error('Error fetching deck:', error);
            setStartCardId(deck.startCardId ?? null);
          }
        } else if (deck.deckId && deck.startCardId) {
          try {
            const deckRef = doc(db, `users/${userId}/decks/${deck.deckId}`);
            const deckSnap = await getDoc(deckRef);
            if (deckSnap.exists()) {
              setFullDeck({ id: deckSnap.id, ...deckSnap.data() });
            }
          } catch (error) {
            console.error('Error fetching deck:', error);
          }
          setStartCardId(deck.startCardId);
        } else if (deck.deckId) {
          try {
            const deckRef = doc(db, `users/${userId}/decks/${deck.deckId}`);
            const deckSnap = await getDoc(deckRef);
            if (deckSnap.exists()) {
              const deckData = { id: deckSnap.id, ...deckSnap.data() };
              setFullDeck({
                ...deckData,
                isTestMode: Boolean(deck.isTestMode),
                trackId: deck.trackId ?? deckData.roadmapTrackId ?? null,
                nodeId: deck.nodeId ?? deckData.roadmapNodeId ?? null
              });
            }
            setStartCardId(null);
          } catch (error) {
            console.error('Error fetching deck:', error);
            setStartCardId(null);
          }
        } else {
          setStartCardId(deck.startCardId ?? null);
          setFullDeck(deck);
        }
      }
    };

    fetchFullDeck();
  }, [deck, userId]);

  const roadmapTrackId = useMemo(() => {
    const fromDeck =
      deck && typeof deck === 'object'
        ? deck.trackId ?? deck.roadmapTrackId ?? null
        : null;
    return fullDeck?.roadmapTrackId ?? fullDeck?.trackId ?? fromDeck ?? null;
  }, [deck, fullDeck]);

  const isRoadmapStudySession = useMemo(
    () =>
      Boolean(
        fullDeck &&
        roadmapTrackId &&
        (isRoadmapUserCookbook(fullDeck) || fullDeck.practiceStartedSession)
      ),
    [fullDeck, roadmapTrackId]
  );

  const derivedPracticeMode = Boolean(
    fullDeck?.practiceStartedSession ||
    (deck && typeof deck === 'object' && deck.practiceStartedSession)
  );

  const [sessionPracticeMode, setSessionPracticeMode] = useState(derivedPracticeMode);

  useEffect(() => {
    if (!isRoadmapStudySession || !roadmapTrackId) {
      setRoadmapTrackMeta(null);
      setRoadmapTrackMetaLoading(false);
      return undefined;
    }

    let cancelled = false;
    setRoadmapTrackMetaLoading(true);
    fetchTrackMeta(roadmapTrackId)
      .then((meta) => {
        if (!cancelled) {
          setRoadmapTrackMeta(meta);
          setRoadmapTrackMetaLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setRoadmapTrackMeta(null);
          setRoadmapTrackMetaLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isRoadmapStudySession, roadmapTrackId]);

  useEffect(() => {
    if (!isRoadmapStudySession || !roadmapTrackId || !userId) {
      setRoadmapTrackProgress(null);
      setRoadmapTrackNodes([]);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const [nodes, progress] = await Promise.all([
          loadTrackNodes(roadmapTrackId),
          loadTrackProgress(userId, roadmapTrackId)
        ]);
        const ordered = buildOrderedNodeGraph(nodes);
        const done = new Set(progress?.completedNodeIds || []);
        const completed = ordered.filter((node) => done.has(node.id)).length;
        if (!cancelled) {
          setRoadmapTrackProgress({ completed, total: ordered.length });
          setRoadmapTrackNodes(ordered);
        }
      } catch {
        if (!cancelled) {
          setRoadmapTrackProgress(null);
          setRoadmapTrackNodes([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isRoadmapStudySession, roadmapTrackId, userId]);

  const roadmapLanguageKeyForTracks = useMemo(
    () =>
      normalizeRoadmapLanguageKey(
        fullDeck?.language || roadmapTrackMeta?.language || ''
      ),
    [fullDeck?.language, roadmapTrackMeta?.language]
  );

  useEffect(() => {
    if (!isRoadmapStudySession || !roadmapLanguageKeyForTracks) {
      setRoadmapLanguageTracks([]);
      setRoadmapLanguageTracksLoading(false);
      return undefined;
    }

    let cancelled = false;
    setRoadmapLanguageTracksLoading(true);
    loadPublishedTracks()
      .then(async (tracks) => {
        if (cancelled) return;
        const filtered = tracks.filter((track) =>
          trackMatchesLanguage(track, roadmapLanguageKeyForTracks)
        );

        let pathEntries = [];
        try {
          const paths = await loadPublishedLearningPaths();
          const pathMeta = pickLearningPathForLanguage(paths, roadmapLanguageKeyForTracks);
          if (pathMeta?.pathId) {
            pathEntries = await loadLearningPathEntries(pathMeta.pathId);
          }
        } catch {
          pathEntries = [];
        }

        const ordered = orderTracksForLanguageRoadmap(
          filtered,
          pathEntries,
          roadmapLanguageKeyForTracks
        );
        setRoadmapLanguageTracks(ordered);
        setRoadmapLanguageTracksLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setRoadmapLanguageTracks([]);
          setRoadmapLanguageTracksLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isRoadmapStudySession, roadmapLanguageKeyForTracks]);

  const studySessionKey = useMemo(() => {
    const deckId =
      fullDeck?.id ||
      (deck && typeof deck === 'object'
        ? deck.deckId ?? deck.id
        : typeof deck === 'string'
          ? deck
          : null);
    const nodeId =
      fullDeck?.roadmapNodeId ??
      fullDeck?.nodeId ??
      (deck && typeof deck === 'object' ? deck.nodeId ?? deck.roadmapNodeId : null) ??
      'node';
    return deckId ? `${deckId}-${nodeId}` : null;
  }, [fullDeck, deck]);

  const derivedTestMode = Boolean(
    (fullDeck && fullDeck.isTestMode) ||
    (deck && typeof deck === 'object' && deck.isTestMode)
  );

  const [sessionTestMode, setSessionTestMode] = useState(derivedTestMode);

  useEffect(() => {
    setSessionTestMode(derivedTestMode);
  }, [studySessionKey]);

  useEffect(() => {
    setSessionPracticeMode(
      Boolean(
        fullDeck?.practiceStartedSession ||
        (deck && typeof deck === 'object' && deck.practiceStartedSession)
      )
    );
  }, [studySessionKey]);

  const autoPracticeEvaluatedRef = useRef(false);

  useEffect(() => {
    autoPracticeEvaluatedRef.current = false;
  }, [studySessionKey]);

  const isTestMode = sessionTestMode;
  const isPracticeSession = sessionPracticeMode;

  const logic = useStudyLogic({
    userId,
    deck: fullDeck,
    startCardId,
    cards: initialCards,
    dueOnly: deck && typeof deck === 'object' && deck.dueOnly === true,
    startedOnly: deck && typeof deck === 'object' && deck.startedOnly === true,
    isTestMode,
    setView,
    previousView,
    onReviewQueueChanged,
    onNodeCompleted,
    roadmapTrackId:
      (deck && typeof deck === 'object' ? (deck.trackId ?? deck.roadmapTrackId) : null) ??
      fullDeck?.roadmapTrackId ??
      fullDeck?.trackId ??
      null,
    roadmapNodeId:
      (deck && typeof deck === 'object' ? (deck.nodeId ?? deck.roadmapNodeId) : null) ??
      fullDeck?.roadmapNodeId ??
      fullDeck?.nodeId ??
      null,
    title,
    disableAiFeatures
  });

  const {
    cards,
    loading,
    currentCardIndex,
    error,
    studyMode,
    setStudyMode,

    showAnswer: _showAnswer,
    setShowAnswer: _setShowAnswer,
    userCode,
    setUserCode,
    feedback,
    setFeedback,
    hasStartedCoding,
    setHasStartedCoding,
    isCorrect,
    setIsCorrect,

    currentLineIndex,
    setCurrentLineIndex,
    lineResults: _lineResults,
    setLineResults: _setLineResults,
    initiallyWrongLines: _initiallyWrongLines,
    setInitiallyWrongLines: _setInitiallyWrongLines,
    isComplete,
    setIsComplete,
    lines,
    setLines: _setLines,
    showLine,
    setShowLine: _setShowLine,
    lineComparison,
    setLineComparison,
    commentHints,
    variableHints,

    notes,
    setNotes: _setNotes,
    isSavingNotes,
    setIsSavingNotes: _setIsSavingNotes,

    explanation,
    setExplanation,
    isExplaining,
    setIsExplaining: _setIsExplaining,
    explanationCode: _explanationCode,
    explanationType: _explanationType,
    flashcardViewMode: _flashcardViewMode,
    setFlashcardViewMode,

    showExperiencePopup,
    setShowExperiencePopup,
    experienceData,
    setExperienceData: _setExperienceData,

    stats,
    setStats: _setStats,
    srs,

    editorContainerRef: _editorContainerRef,
    editorHeight: _editorHeight,
    setEditorHeight: _setEditorHeight,

    loadDeckAndCards: _loadDeckAndCards,
    loadStats: _loadStats,
    loadNotes,
    handleNotesChange,
    updateDailyProgress: _updateDailyProgress,
    updateCourseLevel: _updateCourseLevel,

    resetState,
    resetEditorForRetry,
    resetStateForNewLine,
    handleNext,
    handlePrevious,
    updateCardProgress: _updateCardProgress,

    analyzeCode: _analyzeCode,
    checkCodeSolution: _checkCodeSolution,
    checkLine: _checkLine,

    handleCompletion: _handleCompletion,
    handleSrsAgain: commitSrsAgain,
    saveLineProgress: _saveLineProgress,

    handleExplain,

    handleEditorChange,
    handleEditorChangeLineByLine,

    handleSubmit,
    handleSubmitLineByLine,

    handleRetry: _handleRetry,
    resetStateForRetry: _resetStateForRetry,
    fetchCardData: _fetchCardData,
    initializeCard,

    handleBack,
    navigateToCard,

    testModeSnippetResults,
    testCompleteSummary,
    dismissTestCompleteAndGoBack
  } = logic;

  // ── Local UI state ───────────────────────────────────────────────────
  const [isExpanded, setIsExpanded] = useState(false);
  const [showGlossary, setShowGlossary] = useState(false);
  const [notesViewMode, setNotesViewMode] = useState('notes');
  const [hoveredButtonLabel, setHoveredButtonLabel] = useState(null);
  const [descriptionPageIndex, setDescriptionPageIndex] = useState(0);
  /** True after Again reveals the solution; Try Again clears the editor for another attempt. */
  const [solutionRevealed, setSolutionRevealed] = useState(false);
  /** Learn Mode only: when true, the editor swaps the active line for
   *  a read-only preview of the entire snippet so the user can read
   *  the surrounding code (and ask Byte for full-snippet insights with
   *  the right context). Toggled from the status strip; auto-resets
   *  whenever the active card changes. */
  const [showFullSnippet, setShowFullSnippet] = useState(false);
  /** Per-card session recall status for the progress strip dots. */
  const [sessionCardStatuses, setSessionCardStatuses] = useState([]);
  const sessionRoleExpRef = useRef(new Map());
  const sessionCorrectCountRef = useRef(0);
  const sessionAttemptedCountRef = useRef(0);

  const buildSessionSummaryPayload = useCallback(() => {
    const correctFromState = countCleanSessionCards(sessionCardStatuses);
    const attemptedFromState = countAttemptedSessionCards(sessionCardStatuses);
    return buildStudySessionSummaryPayload({
      correctCount: Math.max(correctFromState, sessionCorrectCountRef.current),
      totalAttempted: Math.max(attemptedFromState, sessionAttemptedCountRef.current),
      roleExpByRole: sessionRoleExpRef.current
    });
  }, [sessionCardStatuses]);

  const openSessionSummaryPanel = useCallback((initialPanel = 'session') => {
    if (!showSessionSummary) return false;
    const payload = buildSessionSummaryPayload();
    if (!shouldShowRegisteredSessionSummary(payload)) return false;
    setExperiencePanel(initialPanel);
    setShowExperiencePopup(true);
    return true;
  }, [showSessionSummary, buildSessionSummaryPayload, setShowExperiencePopup]);

  useEffect(() => {
    if (typeof onRegisterStudyExitHandler !== 'function') return undefined;

    // Leaving study should never re-open the experience modal.
    onRegisterStudyExitHandler({
      requestExit: () => false
    });

    return () => {
      onRegisterStudyExitHandler(null);
    };
  }, [onRegisterStudyExitHandler]);

  useEffect(() => {
    if (!showExperiencePopup) return;
    const updates = experienceData?.roleExpUpdates;
    if (!Array.isArray(updates) || updates.length === 0) return;
    mergeRoleExpUpdatesIntoSession(sessionRoleExpRef.current, updates);
  }, [showExperiencePopup, experienceData]);

  useEffect(() => {
    sessionRoleExpRef.current = new Map();
    sessionCorrectCountRef.current = 0;
    sessionAttemptedCountRef.current = 0;
    setExperiencePanel('rep');
    setRolePathResume(null);
    setSessionCardStatuses([]);
    pendingPracticeAfterSessionRef.current = false;
  }, [studySessionKey]);
  /** Read-only result chip in the footer (correct / wrong / null). */
  const [lastResult, setLastResult] = useState(null);
  /**
   * Marks an in-flight Check submission for a specific card. The
   * shared `isCorrect` state from StudyLogic is unreliable as a
   * "user just submitted" signal — it defaults to `false` and is
   * reset to `false` on every navigation, which used to falsely
   * stamp cards as wrong (and pop a red "missed it" chip in the
   * footer) whenever the user clicked a progress dot or hit Next.
   * We only apply the result when there's a pending submit on the
   * active card.
   */
  const pendingSubmitCardIdRef = useRef(null);
  const rep1TrackedRef = useRef(false);
  const pendingPracticeAfterSessionRef = useRef(false);
  const applyPendingPracticeTransitionRef = useRef(null);
  /** Map of insights by card id; survives card navigation in-session. */
  const insightCacheRef = useRef({});
  const [insightVersion, setInsightVersion] = useState(0);

  const [showHints, setShowHints] = useState(() => {
    try {
      const v = localStorage.getItem('flashcode_study_showHints');
      if (v === 'false') return false;
      if (v === 'true') return true;
    } catch (_) { /* fall through */ }
    return true;
  });

  const [practiceNavMode, setPracticeNavMode] = useState(() => {
    try {
      const v = localStorage.getItem('flashcode_practice_nav_mode');
      if (v === 'index') return 'index';
    } catch (_) { /* fall through */ }
    return 'random';
  });

  const handlePracticeNavModeChange = useCallback((mode) => {
    const next = mode === 'index' ? 'index' : 'random';
    setPracticeNavMode(next);
    try {
      localStorage.setItem('flashcode_practice_nav_mode', next);
    } catch (_) { /* ignore */ }
  }, []);

  const handleToggleHints = () => {
    setShowHints((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('flashcode_study_showHints', String(next));
      } catch (_) { /* ignore */ }
      return next;
    });
  };

  const [showVariableHints, setShowVariableHints] = useState(false);

  const textareaRef = useRef(null);
  const studyModeBeforeNotesRef = useRef('regular');

  // ── Current card derivation ──────────────────────────────────────────
  const currentCard = useMemo(
    () => cards[currentCardIndex] || null,
    [cards, currentCardIndex]
  );

  // ── Init card on mount / mode change ─────────────────────────────────
  useEffect(() => {
    if (cards.length > 0 && studyMode === 'lineByLine') {
      initializeCard();
    }
    if (cards.length > 0) {
      loadNotes();
      setNotesViewMode('notes');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards, currentCardIndex, studyMode]);

  // ── Reset transient UI when the active card changes ──────────────────
  useEffect(() => {
    setShowGlossary(false);
    setShowVariableHints(false);
    setSolutionRevealed(false);
    setShowFullSnippet(false);
    setLastResult(null);
    setHoveredButtonLabel(null);
    setDescriptionPageIndex(0);
    // Any in-flight submission marker becomes invalid the moment the
    // active card changes; clearing it prevents a late `setIsCorrect`
    // from a previous card's submit pipeline from stamping the new
    // card as wrong.
    pendingSubmitCardIdRef.current = null;
  }, [currentCardIndex]);

  // Test Mode has no per-line initializer; clear editor state on every
  // card change so navigation paths that only call `navigateToCard`
  // (e.g. progress dots) cannot carry over draft or Again/Try Again text.
  useEffect(() => {
    if (studyMode !== 'regular') return;
    resetState();
    // resetState is recreated each render; only re-run when the card changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCardIndex, studyMode]);

  // Switching between Test Mode and Learn Mode also exits the full
  // snippet preview — Test Mode already shows the full editor, so the
  // toggle is irrelevant there.
  useEffect(() => {
    if (studyMode !== 'lineByLine') setShowFullSnippet(false);
  }, [studyMode]);

  // ── Track per-card session recall for the progress strip ─────────────
  // Only applies when there's a pending submission marker for the
  // currently active card. Without this guard, every navigation /
  // initial mount would trigger `isCorrect`'s default-`false` value
  // and falsely flag cards as wrong (showing a red "missed it" chip).
  useEffect(() => {
    if (!pendingSubmitCardIdRef.current) return;
    if (pendingSubmitCardIdRef.current !== currentCard?.id) return;
    pendingSubmitCardIdRef.current = null;
    setLastResult(isCorrect ? 'correct' : 'wrong');
    setSessionCardStatuses((prev) => {
      if (isCorrect) {
        const { statuses, firstInteraction, becameClean } = applySessionCardSuccess(
          prev,
          currentCardIndex
        );
        if (firstInteraction) {
          sessionAttemptedCountRef.current += 1;
        }
        if (becameClean) {
          sessionCorrectCountRef.current += 1;
        }
        return statuses;
      }

      const { statuses, firstInteraction } = applySessionCardPenalty(prev, currentCardIndex);
      if (firstInteraction) {
        sessionAttemptedCountRef.current += 1;
      }
      return statuses;
    });
    if (
      isCorrect &&
      typeof onSessionComplete === 'function' &&
      !rep1TrackedRef.current
    ) {
      rep1TrackedRef.current = true;
      trackRep1Completed({
        deck_id: fullDeck?.id || (typeof deck === 'string' ? deck : deck?.id),
        card_id: currentCard?.id,
        language: currentCard?.language,
      });
    }
  }, [isCorrect, currentCard?.id, currentCardIndex, currentCard?.language, deck, fullDeck?.id, onSessionComplete]);

  // Auto-fade the result chip after 2.8s.
  useEffect(() => {
    if (!lastResult) return undefined;
    const id = setTimeout(() => setLastResult(null), 2800);
    return () => clearTimeout(id);
  }, [lastResult]);

  /**
   * Session-completion hook. Registered users use the full cookbook summary
   * (`isCookbookSessionComplete`: every snippet clean and none due today).
   * Guests fire `onSessionComplete` after the first clean Check so the in-app
   * signup prompt can open — new-card learning steps stay “due today”, which
   * would otherwise block conversion forever. Guest fire is synchronous: an
   * in-flight Firestore read that gets cancelled would otherwise swallow the
   * prompt while leaving this ref stuck.
   */
  const sessionCompleteFiredRef = useRef(false);
  useEffect(() => {
    const canCompleteSession =
      typeof onSessionComplete === 'function' || showSessionSummary;
    if (!canCompleteSession) return;
    if (!cards || cards.length === 0) return;
    if (sessionCompleteFiredRef.current) return;

    const guestFirstCleanDone =
      typeof onSessionComplete === 'function' &&
      countCleanSessionCards(sessionCardStatuses) >= 1;
    const registeredCookbookDone =
      showSessionSummary &&
      isCookbookSessionComplete(cards, sessionCardStatuses, cards.length);
    if (!guestFirstCleanDone && !registeredCookbookDone) return;

    const correctCount = countCleanSessionCards(sessionCardStatuses);
    const legacyResults = toLegacySessionResults(sessionCardStatuses);
    const deckId =
      fullDeck?.id ||
      (deck && typeof deck === 'object' ? deck.id || deck.deckId : null) ||
      (typeof deck === 'string' ? deck : null);
    const lastCard = cards[cards.length - 1];

    const fireComplete = (firstNextReviewIso, dueLabel) => {
      if (sessionCompleteFiredRef.current) return;
      sessionCompleteFiredRef.current = true;
      const sessionPayload = buildSessionSummaryPayload();
      const completePayload = {
        results: legacyResults,
        correctCount,
        totalCount: cards.length,
        totalAttempted: sessionPayload.totalAttempted,
        cards,
        firstNextReviewIso,
        dueLabel,
        roleExpUpdates: sessionPayload.roleExpUpdates
      };

      if (
        registeredCookbookDone &&
        shouldTransitionToPracticeAfterSessionComplete({
          isPracticeSession,
          isTestMode,
          showSessionSummary,
          cards
        })
      ) {
        pendingPracticeAfterSessionRef.current = true;
      }

      if (showSessionSummary && shouldShowRegisteredSessionSummary(sessionPayload)) {
        openSessionSummaryPanel('session');
        return;
      }

      if (typeof onSessionComplete === 'function') {
        onSessionComplete(completePayload);
      }

      if (pendingPracticeAfterSessionRef.current) {
        setTimeout(() => applyPendingPracticeTransitionRef.current?.(), 0);
      }
    };

    // Stay on the study surface; App shows the top save banner from this callback.
    if (guestFirstCleanDone && !showSessionSummary) {
      fireComplete(null, null);
      return undefined;
    }

    if (!srs || !deckId || !lastCard?.id || !userId) {
      fireComplete(null, null);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      let firstNextReviewIso = null;
      try {
        const progressRef = doc(db, `users/${userId}/progress/${deckId}_${lastCard.id}`);
        const snap = await getDoc(progressRef);
        if (!cancelled && snap.exists()) {
          firstNextReviewIso = snap.data()?.nextReview || null;
        }
      } catch (err) {
        console.warn('[Study] session-complete progress read failed:', err);
      }
      if (!cancelled) {
        fireComplete(
          firstNextReviewIso,
          formatGuestDueLabel(firstNextReviewIso)
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    sessionCardStatuses,
    cards,
    onSessionComplete,
    showSessionSummary,
    openSessionSummaryPanel,
    deck,
    fullDeck?.id,
    srs,
    userId,
    buildSessionSummaryPayload,
    isPracticeSession,
    isTestMode
  ]);

  const handleLeaveStudy = useCallback(() => {
    handleBack();
  }, [handleBack]);

  // ── Insight cache: explanation ↔ per-card·scope storage ──────────────
  // Insights for the active line and for the full snippet are different
  // artifacts — caching both under one key would either show stale data
  // or force regeneration on every view toggle. Cache by composite
  // `${cardId}:${scope}` instead, where scope is `full` whenever the
  // user is reading the whole snippet (Test Mode, or Learn Mode with
  // the full-snippet preview toggled on) and `line` otherwise.
  const insightScope =
    studyMode === 'lineByLine' && !showFullSnippet ? 'line' : 'full';
  const insightCacheKey = currentCard?.id
    ? `${currentCard.id}:${insightScope}`
    : null;

  // Cache-write must only fire on real `explanation` changes — never on
  // cache-key changes alone. Otherwise toggling between line and
  // full-snippet scope would write the previous scope's explanation
  // under the new key (because `explanation` lingers in state for one
  // render before the cache-load effect clears it), permanently
  // contaminating the other scope's cache.
  useEffect(() => {
    if (explanation && insightCacheKey) {
      insightCacheRef.current[insightCacheKey] = explanation;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [explanation]);

  useEffect(() => {
    if (!currentCard?.id) return;
    const cached = insightCacheKey
      ? insightCacheRef.current[insightCacheKey]
      : null;
    if (cached) {
      setExplanation(cached);
    } else {
      setExplanation('');
    }
    setFlashcardViewMode('study');
    setInsightVersion((v) => v + 1);
  }, [currentCard?.id, insightCacheKey, setExplanation, setFlashcardViewMode]);

  const resolvePracticeNextCardIndex = useCallback(
    (cardsForNavigation, excludeCardId, currentIndex) =>
      practiceNavMode === 'index'
        ? findNextIndexCookbookCardIndex(cardsForNavigation, currentIndex)
        : findRandomPracticeCookbookCardIndex(cardsForNavigation, excludeCardId),
    [practiceNavMode]
  );

  const resolvePostCompleteCardIndex = useCallback(
    (cardsForNavigation, excludeCardId) => {
      if (!isPracticeSession) {
        return findNextEarliestReviewCookbookCardIndex(cardsForNavigation, excludeCardId);
      }

      const currentIndex = cardsForNavigation.findIndex((card) => card.id === excludeCardId);
      return resolvePracticeNextCardIndex(
        cardsForNavigation,
        excludeCardId,
        currentIndex >= 0 ? currentIndex : 0
      );
    },
    [isPracticeSession, resolvePracticeNextCardIndex]
  );

  const handleExperiencePopupDismiss = useCallback(() => {
    const wasSessionPanel = experiencePanel === 'session';
    const fromReviewPage = previousView === 'home-srs';
    setShowExperiencePopup(false);
    setExperiencePanel('rep');
    if (experienceData?.stillInLearning) {
      resetState();
      setUserCode('');
      setHasStartedCoding(false);
      setFeedback('');
      return;
    }
    if (pendingPracticeAfterSessionRef.current) {
      applyPendingPracticeTransitionRef.current?.();
      return;
    }

    if (wasSessionPanel && fromReviewPage && typeof onExitStudy === 'function') {
      onExitStudy('home-srs');
      return;
    }

    const cardsForNavigation = experienceData?.committedSrsProgress && currentCard?.id
      ? cards.map((card) => (
        card.id === currentCard.id
          ? {
            ...card,
            progress: {
              ...(card.progress || {}),
              ...experienceData.committedSrsProgress
            }
          }
          : card
      ))
      : cards;

    const nextIndex = resolvePostCompleteCardIndex(cardsForNavigation, currentCard?.id);
    const nextCard = nextIndex >= 0 ? cardsForNavigation[nextIndex] : null;
    if (nextCard?.id && nextCard.id !== currentCard?.id) {
      navigateToCard(nextCard.id);
      resetState();
      setUserCode('');
      setHasStartedCoding(false);
      setFeedback('');
      setSolutionRevealed(false);
      return;
    }

    if (fromReviewPage && typeof onExitStudy === 'function') {
      onExitStudy('home-srs');
    }
  }, [
    previousView,
    onExitStudy,
    experiencePanel,
    setShowExperiencePopup,
    experienceData?.stillInLearning,
    experienceData?.committedSrsProgress,
    currentCard?.id,
    cards,
    navigateToCard,
    resetState,
    setUserCode,
    setHasStartedCoding,
    setFeedback,
    setSolutionRevealed,
    resolvePostCompleteCardIndex
  ]);

  const handleContinueRolePath = useCallback(() => {
    pendingPracticeAfterSessionRef.current = false;
    setShowExperiencePopup(false);
    setExperiencePanel('rep');
    onNavigateRolePath?.(rolePathResume);
  }, [onNavigateRolePath, rolePathResume, setShowExperiencePopup]);

  const sessionSummaryPayload = useMemo(
    () => buildSessionSummaryPayload(),
    [buildSessionSummaryPayload]
  );

  const sessionHasRoleExp = (sessionSummaryPayload?.roleExpUpdates?.length ?? 0) > 0;

  const continuePathLabel = useMemo(() => {
    if (rolePathResume?.allComplete) return 'View path';
    if (rolePathResume?.resumeTrackId) return 'Continue path';
    return 'View path';
  }, [rolePathResume]);

  useEffect(() => {
    if (
      !showExperiencePopup ||
      experiencePanel !== 'session' ||
      !sessionHasRoleExp ||
      !primaryRoleId ||
      !userId
    ) {
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const catalogTracks = await loadPublishedTracks();
        const role = publishedRoles.find((item) => item.roleId === primaryRoleId);
        if (!role) return;
        const overview = await computeRolePathOverview(userId, role, catalogTracks);
        if (!cancelled) {
          setRolePathResume(overview);
        }
      } catch (err) {
        console.warn('[Study] role path resume failed:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showExperiencePopup, experiencePanel, sessionHasRoleExp, primaryRoleId, userId, publishedRoles]);

  useEffect(() => {
    setExperiencePanel('rep');
  }, [experienceData?.snippetExp]);

  // ── Learn-mode → test-mode transition after experience popup closes ──
  const handleExperiencePopupClose = useCallback(() => {
    if (previousView === 'home-srs') {
      return;
    }
    if (studyMode === 'lineByLine' && isComplete) {
      setCurrentLineIndex(0);
      setStudyMode('regular');
      setIsComplete(false);
      resetState();
      setUserCode('');
      setHasStartedCoding(false);

      setTimeout(() => {
        if (textareaRef.current) textareaRef.current.focus();
      }, 100);
    }
  }, [
    previousView,
    studyMode,
    isComplete,
    setCurrentLineIndex,
    setStudyMode,
    setIsComplete,
    resetState,
    setUserCode,
    setHasStartedCoding
  ]);

  useEffect(() => {
    if (!showExperiencePopup && studyMode === 'lineByLine' && isComplete) {
      const id = setTimeout(handleExperiencePopupClose, 2000);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [showExperiencePopup, studyMode, isComplete, handleExperiencePopupClose]);

  // ── Mode change handler ──────────────────────────────────────────────
  const handleModeChange = (newMode) => {
    const wasLearnMode = studyMode === 'lineByLine';
    setStudyMode(newMode);
    resetState();
    setUserCode('');
    setHasStartedCoding(false);

    if (newMode === 'lineByLine') {
      initializeCard();
      requestAnimationFrame(() => {
        if (textareaRef.current) {
          textareaRef.current.blur();
          setUserCode('');
          setHasStartedCoding(false);
        }
      });
    } else if (wasLearnMode) {
      // Leaving Learn Mode: a long Byte explanation can leave the
      // document and the byte-companion body scrolled past the
      // viewport. Reset both so the next mode lands on a clean,
      // unscrolled layout instead of inheriting Learn's scroll state.
      requestAnimationFrame(() => {
        try {
          window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
          const byteBody = document.querySelector('.byte-companion__body');
          if (byteBody) byteBody.scrollTop = 0;
        } catch (_) {
          /* noop */
        }
      });
    }
  };

  const handleSessionModeChange = useCallback(
    (mode) => {
      if (mode === 'practice') {
        setSessionPracticeMode(true);
        if (studyMode === 'lineByLine' || studyMode === 'notes') {
          handleModeChange('regular');
        }
        return;
      }

      setSessionPracticeMode(false);
      const targetStudyMode = mode === 'learn' ? 'lineByLine' : 'regular';
      if (studyMode !== targetStudyMode) {
        handleModeChange(targetStudyMode);
      }
    },
    [studyMode]
  );

  const sessionMode = useMemo(
    () =>
      sessionPracticeMode ? 'practice' : studyMode === 'lineByLine' ? 'learn' : 'test',
    [sessionPracticeMode, studyMode]
  );

  const applyPendingPracticeTransition = useCallback(() => {
    if (!pendingPracticeAfterSessionRef.current) return false;
    pendingPracticeAfterSessionRef.current = false;

    handleSessionModeChange('practice');

    const currentIndex = cards.findIndex((card) => card.id === currentCard?.id);
    const nextIndex = resolvePracticeNextCardIndex(
      cards,
      currentCard?.id,
      currentIndex >= 0 ? currentIndex : 0
    );
    const nextCard = nextIndex >= 0 ? cards[nextIndex] : null;
    if (nextCard?.id && nextCard.id !== currentCard?.id) {
      navigateToCard(nextCard.id);
    }

    resetState();
    setUserCode('');
    setHasStartedCoding(false);
    setFeedback('');
    setSolutionRevealed(false);
    return true;
  }, [
    cards,
    currentCard?.id,
    handleSessionModeChange,
    navigateToCard,
    resetState,
    resolvePracticeNextCardIndex,
    setFeedback,
    setHasStartedCoding,
    setSolutionRevealed,
    setUserCode
  ]);

  useEffect(() => {
    applyPendingPracticeTransitionRef.current = applyPendingPracticeTransition;
  }, [applyPendingPracticeTransition]);

  useEffect(() => {
    if (autoPracticeEvaluatedRef.current) return;
    if (loading || cards.length === 0) return;
    if (isTestMode) return;
    if (derivedPracticeMode) return;
    if (deck && typeof deck === 'object' && deck.practiceStartedSession) return;
    if (deck && typeof deck === 'object' && deck.dueOnly) return;

    autoPracticeEvaluatedRef.current = true;

    if (areAllCookbookProgressDotsGreen(cards)) {
      handleSessionModeChange('practice');
    }
  }, [
    loading,
    cards,
    isTestMode,
    derivedPracticeMode,
    deck,
    handleSessionModeChange
  ]);

  // ── Notes / Glossary toggling (use existing `studyMode === 'notes'`) ─
  const isNotesOpen = studyMode === 'notes';
  const handleToggleNotes = () => {
    if (isNotesOpen) {
      handleModeChange(studyModeBeforeNotesRef.current || 'regular');
    } else {
      if (studyMode !== 'notes') {
        studyModeBeforeNotesRef.current = studyMode;
      }
      setShowGlossary(false);
      setShowVariableHints(false);
      handleModeChange('notes');
    }
  };

  const handleToggleVariableHints = useCallback(() => {
    if (showVariableHints) {
      setShowVariableHints(false);
      return;
    }
    if (isNotesOpen) {
      handleModeChange(studyModeBeforeNotesRef.current || 'regular');
    }
    setShowGlossary(false);
    setUserCode('');
    setHasStartedCoding(false);
    setLineComparison(null);
    setShowVariableHints(true);
  }, [
    showVariableHints,
    isNotesOpen,
    handleModeChange,
    setUserCode,
    setHasStartedCoding,
    setLineComparison
  ]);

  const handleToggleGlossary = () => {
    if (!showGlossary) {
      setShowVariableHints(false);
    }
    setShowGlossary((prev) => !prev);
  };

  const handleOpenCookbookIndex = useCallback(() => {
    if (showGlossary) {
      setShowGlossary(false);
      return;
    }
    if (studyMode === 'notes') {
      handleModeChange('regular');
    }
    setShowVariableHints(false);
    setShowGlossary(true);
  }, [showGlossary, studyMode, handleModeChange]);

  const handleExpandCard = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setIsExpanded((prev) => !prev);
  };

  /**
   * Again: Anki-style lapse — grade as "again", reveal solution, penalize session dot.
   */
  const handleAgain = useCallback(async () => {
    if (!currentCard || solutionRevealed || isTestMode) return;

    const cardProgress = currentCard?.progress;
    const srsSchedulingPaused = isSrsPracticeOnlyForStudy(cardProgress);
    const shouldRecordSrsVisit = commitSrsAgain
      && (!srsSchedulingPaused || isFirstExposurePending(cardProgress));
    const srsResult = shouldRecordSrsVisit ? await commitSrsAgain() : null;

    setSessionCardStatuses((prev) => {
      const { statuses, firstInteraction } = applySessionCardPenalty(prev, currentCardIndex);
      if (firstInteraction) {
        sessionAttemptedCountRef.current += 1;
      }
      return statuses;
    });
    setLastResult('wrong');
    if (srsSchedulingPaused) {
      setFeedback('Again · practice only');
    } else {
      const scheduleLabel =
        srsResult?.lastRating === 'again' ? formatSrsScheduleLabel(srsResult) : '';
      setFeedback(scheduleLabel ? `Again · ${scheduleLabel}` : 'Again');
    }

    const solutionText =
      studyMode === 'lineByLine'
        ? currentCard.solution ||
        (Array.isArray(lines) ? lines.join('\n') : '')
        : currentCard.solution || '';

    setUserCode(solutionText);
    setHasStartedCoding(true);
    if (studyMode === 'lineByLine') setLineComparison(null);
    setSolutionRevealed(true);
    setIsCorrect(null);
  }, [
    currentCard,
    solutionRevealed,
    isTestMode,
    studyMode,
    lines,
    currentCardIndex,
    commitSrsAgain,
    setUserCode,
    setHasStartedCoding,
    setLineComparison,
    setIsCorrect,
    setFeedback
  ]);

  const handleTryAgain = useCallback(() => {
    if (!currentCard || !solutionRevealed) return;

    resetEditorForRetry();
    setSolutionRevealed(false);
    setIsCorrect(null);
  }, [
    currentCard,
    solutionRevealed,
    resetEditorForRetry,
    setIsCorrect
  ]);

  const nextUnstartedIndex = useMemo(
    () => findNextUnstartedCookbookCardIndex(cards, currentCardIndex),
    [cards, currentCardIndex]
  );

  const progressNextUnstartedDisabled =
    cards.length <= 1 || nextUnstartedIndex < 0;

  const practiceNextAvailable = isPracticeSession && cards.length > 1;
  const progressNextAfterRevealDisabled = isPracticeSession
    ? !practiceNextAvailable
    : progressNextUnstartedDisabled;
  const progressNextAfterRevealHighlighted =
    solutionRevealed && !progressNextAfterRevealDisabled;
  const progressNextAfterRevealLabel = isPracticeSession
    ? practiceNavMode === 'index'
      ? 'Next in order'
      : 'Random snippet'
    : 'Next unstarted snippet';

  const handleProgressNext = useCallback(() => {
    if (solutionRevealed) {
      if (isPracticeSession) {
        if (cards.length <= 1) return;

        const nextIndex = resolvePracticeNextCardIndex(cards, currentCard?.id, currentCardIndex);
        const targetCard = nextIndex >= 0 ? cards[nextIndex] : null;
        if (targetCard?.id && typeof navigateToCard === 'function') {
          navigateToCard(targetCard.id);
          resetState();
        }
        setSolutionRevealed(false);
        setFeedback('');
        return;
      }

      if (progressNextUnstartedDisabled) return;

      const targetCard = cards[nextUnstartedIndex];
      if (targetCard?.id && typeof navigateToCard === 'function') {
        navigateToCard(targetCard.id);
        resetState();
      }
      setSolutionRevealed(false);
      setFeedback('');
      return;
    }

    handleNext();
  }, [
    solutionRevealed,
    isPracticeSession,
    resolvePracticeNextCardIndex,
    progressNextUnstartedDisabled,
    cards,
    currentCard?.id,
    nextUnstartedIndex,
    navigateToCard,
    resetState,
    setFeedback,
    handleNext
  ]);

  // ── Insight generation (writes through cache) ────────────────────────
  const isInsightLoading = isExplaining && !explanation;
  const cachedInsight = insightCacheKey
    ? insightCacheRef.current[insightCacheKey]
    : null;
  const activeInsight = explanation || cachedInsight || '';

  const handleGenerateInsights = useCallback(() => {
    if (!currentCard?.id) return;
    if (cachedInsight) {
      setExplanation(cachedInsight);
      return;
    }
    // When the user is reading the full snippet, force a whole-code
    // explanation; without this Learn Mode would still call
    // `explainLine` against `lines[currentLineIndex]` and produce a
    // single-line insight that contradicts what's on screen.
    const wantsFullSnippet = insightScope === 'full';
    handleExplain(true, wantsFullSnippet);
  }, [
    currentCard?.id,
    cachedInsight,
    insightScope,
    setExplanation,
    handleExplain
  ]);

  /**
   * Force a fresh insight regardless of cache state. Used by the
   * "Regenerate" affordance inside the Byte panel — important when a
   * cached insight no longer reflects what's on screen (e.g. the user
   * has updated their solution, switched scope, or simply wants a
   * different perspective from Byte).
   */
  const handleRegenerateInsights = useCallback(() => {
    if (!currentCard?.id || !insightCacheKey) return;
    delete insightCacheRef.current[insightCacheKey];
    setExplanation('');
    const wantsFullSnippet = insightScope === 'full';
    handleExplain(true, wantsFullSnippet);
  }, [
    currentCard?.id,
    insightCacheKey,
    insightScope,
    setExplanation,
    handleExplain
  ]);

  // ── Chat: graceful no-op stub (real chat ships in a follow-up PR) ────
  const handleAskByte = useCallback(
    async (_prompt) => {
      // No backend yet — ByteChat falls back to its placeholder reply.
      return null;
    },
    []
  );

  // ── Submit / Copy / Again wiring ──────────────────────────────────────
  const handleStudySubmit = () => {
    if (solutionRevealed) {
      const solutionText = (currentCard?.solution || '').trim();
      const typed = userCode.trim();
      if (!typed || typed === solutionText) {
        setFeedback('Click Try Again to clear the solution, then enter your answer and Check.');
        return;
      }
      setSolutionRevealed(false);
    }

    if (studyMode === 'lineByLine') {
      // Mark the active card as having an in-flight submit so the
      // result-tracking effect knows to consume the next `isCorrect`
      // change (and ignore default/reset transitions).
      pendingSubmitCardIdRef.current = currentCard?.id ?? null;
      handleSubmitLineByLine();
      return;
    }

    // Capture whether this click actually validates typed code.
    // `handleSubmit` is overloaded — with an empty editor it instead
    // reveals the solution (or shows a "type your answer" hint in
    // Test Mode), and neither of those should wipe the editor.
    // After a prior submit, `showAnswer` may still be true; only count
    // a real re-attempt when the user has typed new code (or clear
    // showAnswer via handleEditorChange). Empty Check after success
    // must not stamp the card as wrong via the result-tracking effect.
    const willValidate = Boolean(userCode.trim()) && hasStartedCoding;
    if (willValidate) {
      pendingSubmitCardIdRef.current = currentCard?.id ?? null;
    }
    handleSubmit();
    if (willValidate) {
      // `checkCodeSolution` reads `userCode` synchronously off its
      // closure before any await, so clearing right after the call is
      // safe and won't affect the in-flight comparison.
      setUserCode('');
      setHasStartedCoding(false);
    }
  };

  const handleCopySolution = useCallback(async () => {
    if (!currentCard?.solution) return;
    try {
      await navigator.clipboard.writeText(currentCard.solution);
    } catch (err) {
      console.warn('Copy failed:', err);
    }
  }, [currentCard?.solution]);

  const handleUpgradeClick = () => {
    if (disableAiFeatures && typeof onGuestUpgrade === 'function') {
      onGuestUpgrade();
      return;
    }
    if (typeof setView === 'function') setView('subscription');
  };

  const handleProgressDotClick = (index) => {
    const target = cards[index];
    if (target && typeof navigateToCard === 'function') {
      navigateToCard(target.id);
      resetState();
    }
  };

  // ── Language detection (preserved from previous implementation) ──────
  const normalizeLanguage = (lang) => {
    if (!lang) return 'javascript';
    const l = String(lang).toLowerCase();
    if (l.includes('c++') || l === 'cpp') return 'cpp';
    if (l.includes('c#') || l === 'csharp') return 'csharp';
    if (l === 'js' || l.includes('javascript')) return 'javascript';
    if (l === 'ts' || l.includes('typescript')) return 'typescript';
    if (l.includes('python') || l === 'py') return 'python';
    if (l.includes('java')) return 'java';
    if (l.includes('ruby')) return 'ruby';
    if (l === 'go' || l.includes('golang')) return 'go';
    if (l.includes('php')) return 'php';
    if (l.includes('rust')) return 'rust';
    if (l.includes('kotlin')) return 'kotlin';
    if (l.includes('swift')) return 'swift';
    return l;
  };

  const detectLanguageFromCode = (code) => {
    if (!code) return 'javascript';
    const codeStr = code.toLowerCase();
    if (
      codeStr.includes('import foundation') ||
      codeStr.includes('import swiftui') ||
      codeStr.includes('guard let') ||
      codeStr.includes('guard var') ||
      /\bfunc\s+\w+/.test(codeStr) ||
      (/\blet\s+\w+/.test(codeStr) && !codeStr.includes('const ')) ||
      (codeStr.includes('for ') && codeStr.includes(' in ') && codeStr.includes('{') && !codeStr.includes('def '))
    ) {
      return 'swift';
    }
    if (codeStr.includes('def ') || codeStr.includes('if __name__') ||
      codeStr.includes('#!/usr/bin/python') ||
      (codeStr.includes('print(') && (codeStr.includes('from ') || codeStr.includes('import ') && !codeStr.includes('import foundation')))) {
      return 'python';
    }
    if (codeStr.includes('function ') || codeStr.includes('const ') ||
      codeStr.includes('let ') || codeStr.includes('var ') ||
      codeStr.includes('console.log') || codeStr.includes('=>')) return 'javascript';
    if (codeStr.includes('public class') || codeStr.includes('public static void main') ||
      codeStr.includes('system.out.println')) return 'java';
    if (codeStr.includes('#include') || codeStr.includes('std::') ||
      codeStr.includes('using namespace') || codeStr.includes('int main()')) return 'cpp';
    if (codeStr.includes('using system') || codeStr.includes('namespace ')) return 'csharp';
    if ((codeStr.includes('def ') && codeStr.includes('end')) ||
      codeStr.includes('puts ') || codeStr.includes('require ')) return 'ruby';
    if (codeStr.includes('<?php') || codeStr.includes('echo ')) return 'php';
    if (codeStr.includes('package ') || codeStr.includes('func main()') ||
      codeStr.includes('fmt.println')) return 'go';
    if (codeStr.includes('fn main()') || codeStr.includes('println!')) return 'rust';
    if (codeStr.includes('interface ') || codeStr.includes(': string') ||
      codeStr.includes(': number')) return 'typescript';
    return 'javascript';
  };

  const extractLanguageFromContent = (card) => {
    if (card?.language) return normalizeLanguage(card.language);
    if (card?.snippetLanguage) return normalizeLanguage(card.snippetLanguage);
    if (fullDeck?.language) return normalizeLanguage(fullDeck.language);
    if (deck?.language) return normalizeLanguage(deck.language);
    if (isRoadmapStudySession && roadmapTrackMeta?.language) {
      return normalizeLanguage(roadmapTrackMeta.language);
    }
    if (card?.solution) {
      const detected = detectLanguageFromCode(card.solution);
      if (detected !== 'javascript') return detected;
    }
    return 'javascript';
  };

  const resolvedVariableHints = useMemo(() => {
    if (studyMode === 'lineByLine' && variableHints.length > 0) {
      return variableHints;
    }
    if (!currentCard?.solution) return [];
    return extractVariableHints(
      currentCard.solution,
      extractLanguageFromContent(currentCard)
    );
  }, [studyMode, variableHints, currentCard]);

  const visibleVariableHints = useMemo(() => {
    if (!resolvedVariableHints.length) return [];
    if (studyMode === 'lineByLine') {
      return resolvedVariableHints.filter((hint) => hint.lineIndex < currentLineIndex);
    }
    return resolvedVariableHints;
  }, [resolvedVariableHints, studyMode, currentLineIndex]);

  const getDisplayLanguage = (card) => {
    const lang = extractLanguageFromContent(card);
    switch (lang) {
      case 'javascript': return 'JavaScript';
      case 'python': return 'Python';
      case 'java': return 'Java';
      case 'cpp': return 'C++';
      case 'csharp': return 'C#';
      case 'ruby': return 'Ruby';
      case 'typescript': return 'TypeScript';
      case 'go': return 'Go';
      case 'php': return 'PHP';
      case 'rust': return 'Rust';
      case 'kotlin': return 'Kotlin';
      case 'swift': return 'Swift';
      default: return 'JavaScript';
    }
  };

  // ── Blur the line-by-line textarea when the line index advances ─────
  useEffect(() => {
    if (studyMode === 'lineByLine' && textareaRef.current) {
      textareaRef.current.blur();
    }
  }, [currentLineIndex, studyMode]);

  const handleViewRoadmapTrack = useCallback(() => {
    if (typeof setView === 'function') {
      setView('roadmap');
    }
    const langKey = normalizeRoadmapLanguageKey(
      fullDeck?.language || roadmapTrackMeta?.language || ''
    );
    const search = langKey
      ? createSearchParams({
        [ROADMAP_GRAPH_LANG_SEARCH_PARAM]: langKey
      }).toString()
      : '';
    navigate({
      pathname: ROADMAP_PATHS.graph,
      ...(search ? { search } : {}),
      state: roadmapTrackId
        ? { [ROADMAP_GRAPH_SELECTED_TRACK_ID_STATE_KEY]: roadmapTrackId }
        : undefined
    });
  }, [
    navigate,
    fullDeck?.language,
    roadmapTrackMeta?.language,
    roadmapTrackId,
    setView
  ]);

  const currentRoadmapNodeId = useMemo(
    () =>
      fullDeck?.roadmapNodeId ??
      fullDeck?.nodeId ??
      (deck && typeof deck === 'object' ? deck.nodeId ?? deck.roadmapNodeId : null),
    [fullDeck, deck]
  );

  const roadmapNodeNav = useMemo(() => {
    const total = roadmapTrackNodes.length;
    if (!total || !currentRoadmapNodeId) {
      return { index: -1, current: null, prev: null, next: null, total };
    }
    const index = roadmapTrackNodes.findIndex((node) => node.id === currentRoadmapNodeId);
    if (index < 0) {
      return { index: -1, current: null, prev: null, next: null, total };
    }
    return {
      index,
      current: roadmapTrackNodes[index],
      prev: index > 0 ? roadmapTrackNodes[index - 1] : null,
      next: index < total - 1 ? roadmapTrackNodes[index + 1] : null,
      total
    };
  }, [roadmapTrackNodes, currentRoadmapNodeId]);

  const handleNavigateRoadmapNode = useCallback(
    async (node, { trackId = roadmapTrackId, trackMeta = roadmapTrackMeta } = {}) => {
      if (
        !node?.id ||
        !node?.snippetIds?.length ||
        !onStartStudying ||
        !userId ||
        !trackId ||
        roadmapNodeNavLoading
      ) {
        return;
      }

      setRoadmapNodeNavLoading(true);
      try {
        const snippets = await loadSnippetsByIds(node.snippetIds);
        if (snippets.length === 0) return;

        const langKey = normalizeRoadmapLanguageKey(
          fullDeck?.language || trackMeta?.language || ''
        );
        const trackTitleForCookbook = trackMeta?.title
          ? formatRoadmapTrackTitle(langKey, trackMeta.title, {
            language: trackMeta.language || langKey
          })
          : trackId;
        const session = await ensureRoadmapCookbookForNode(
          userId,
          trackId,
          node,
          snippets,
          langKey,
          trackTitleForCookbook
        );

        if (isTestMode) {
          onStartStudying({
            deckId: session.cookbookId,
            cards: session.cards,
            isTestMode: true,
            trackId,
            nodeId: node.id
          });
        } else {
          onStartStudying({
            deckId: session.cookbookId,
            cards: session.cards,
            trackId,
            nodeId: node.id
          });
        }
      } catch (err) {
        console.error('Study: failed to navigate track node', err);
      } finally {
        setRoadmapNodeNavLoading(false);
      }
    },
    [
      onStartStudying,
      userId,
      roadmapTrackId,
      roadmapNodeNavLoading,
      fullDeck?.language,
      roadmapTrackMeta,
      isTestMode
    ]
  );

  const handleNavigateTrackNode = useCallback(
    (node) => handleNavigateRoadmapNode(node),
    [handleNavigateRoadmapNode]
  );

  const handleSelectRoadmapTrack = useCallback(
    async (nextTrackId) => {
      if (
        !nextTrackId ||
        nextTrackId === roadmapTrackId ||
        !onStartStudying ||
        !userId ||
        roadmapNodeNavLoading
      ) {
        return;
      }

      const selectedTrack =
        roadmapLanguageTracks.find((track) => track.trackId === nextTrackId) || null;
      if (!selectedTrack) return;
      if (!userCanAccessRoadmapTrack(isPremiumUser, nextTrackId, selectedTrack)) {
        handleSubscriptionRequired();
        return;
      }

      setRoadmapNodeNavLoading(true);
      try {
        const nodes = buildOrderedNodeGraph(await loadTrackNodes(nextTrackId));
        if (!nodes.length) {
          setRoadmapNodeNavLoading(false);
          return;
        }

        const currentIndex = roadmapNodeNav.index >= 0 ? roadmapNodeNav.index : 0;
        const targetNode = nodes[Math.min(currentIndex, nodes.length - 1)];
        await handleNavigateRoadmapNode(targetNode, {
          trackId: nextTrackId,
          trackMeta: selectedTrack
        });
      } catch (err) {
        console.error('Study: failed to switch roadmap track', err);
        setRoadmapNodeNavLoading(false);
      }
    },
    [
      roadmapTrackId,
      onStartStudying,
      userId,
      roadmapNodeNavLoading,
      roadmapLanguageTracks,
      roadmapNodeNav.index,
      handleNavigateRoadmapNode,
      isPremiumUser,
      handleSubscriptionRequired
    ]
  );

  const handleSelectRoadmapTopic = useCallback(
    (topicId) => {
      if (!topicId || topicId === currentRoadmapNodeId || roadmapNodeNavLoading) {
        return;
      }
      const node = roadmapTrackNodes.find((item) => item.id === topicId);
      if (node) {
        handleNavigateTrackNode(node);
      }
    },
    [
      currentRoadmapNodeId,
      roadmapNodeNavLoading,
      roadmapTrackNodes,
      handleNavigateTrackNode
    ]
  );

  const fullDescriptionText =
    currentCard?.description ||
    (fullDeck?.title ? fullDeck.title : 'Practice this snippet');
  const descriptionPages = useMemo(() => {
    const pages = splitDescriptionPages(fullDescriptionText);
    return pages.length > 0 ? pages : ['Practice this snippet'];
  }, [fullDescriptionText]);
  const hasMultipleDescriptionPages = descriptionPages.length > 1;
  const clampedDescriptionPageIndex = Math.min(
    descriptionPageIndex,
    descriptionPages.length - 1
  );
  const descriptionPageText = descriptionPages[clampedDescriptionPageIndex];

  const handleDescriptionPageClick = useCallback(
    (e) => {
      if (!hasMultipleDescriptionPages) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const isLeftHalf = e.clientX - rect.left < rect.width / 2;
      setDescriptionPageIndex((prev) => {
        const safePrev = Math.min(prev, descriptionPages.length - 1);
        if (isLeftHalf) return Math.max(0, safePrev - 1);
        return Math.min(descriptionPages.length - 1, safePrev + 1);
      });
    },
    [descriptionPages.length, hasMultipleDescriptionPages]
  );

  const handleDescriptionPageKeyDown = useCallback(
    (e) => {
      if (!hasMultipleDescriptionPages) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setDescriptionPageIndex((prev) => Math.max(0, prev - 1));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setDescriptionPageIndex((prev) =>
          Math.min(descriptionPages.length - 1, prev + 1)
        );
      }
    },
    [descriptionPages.length, hasMultipleDescriptionPages]
  );

  const handleDescriptionMetaMouseMove = useCallback(
    (e) => {
      if (!hasMultipleDescriptionPages) {
        e.currentTarget.style.cursor = '';
        return;
      }
      const rect = e.currentTarget.getBoundingClientRect();
      const isLeftHalf = e.clientX - rect.left < rect.width / 2;
      const atStart = clampedDescriptionPageIndex === 0;
      const atEnd = clampedDescriptionPageIndex === descriptionPages.length - 1;
      const canNavigate = isLeftHalf ? !atStart : !atEnd;
      e.currentTarget.style.cursor = canNavigate ? 'pointer' : 'default';
    },
    [
      clampedDescriptionPageIndex,
      descriptionPages.length,
      hasMultipleDescriptionPages
    ]
  );

  const roadmapTrackOptions = useMemo(() => {
    const langKey = roadmapLanguageKeyForTracks;
    const sourceTracks = roadmapLanguageTracks.length
      ? roadmapLanguageTracks
      : roadmapTrackMeta
        ? [{ trackId: roadmapTrackId, title: roadmapTrackMeta.title, language: roadmapTrackMeta.language }]
        : [];

    return sourceTracks
      .map((track) => ({
        value: track.trackId,
        label: formatRoadmapTrackTitle(langKey, track.title, track) || formatTrackTitle(track.title),
        locked: !userCanAccessRoadmapTrack(isPremiumUser, track.trackId, track)
      }))
      .filter((option) => option.value && option.label);
  }, [
    roadmapLanguageKeyForTracks,
    roadmapLanguageTracks,
    roadmapTrackMeta,
    roadmapTrackId,
    isPremiumUser
  ]);

  const roadmapTopicOptions = useMemo(
    () =>
      roadmapTrackNodes.map((node) => ({
        value: node.id,
        label: formatTrackTitle(node.title)
      })),
    [roadmapTrackNodes]
  );

  // ── Loading / error / empty short-circuits ──────────────────────────
  if (loading) {
    return <StudyLoadingSkeleton />;
  }

  if (error) {
    return (
      <div className="study-viewer">
        <div className="study-error">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={handleLeaveStudy} className="study-back-button">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!currentCard) {
    return <div className="study-viewer" />;
  }

  // ── Derived display values ──────────────────────────────────────────
  const navStreak = stats?.currentStreak ?? null;
  const cookbookName = isPracticeSession
    ? (fullDeck?.roadmapTrackId || fullDeck?.trackId
      ? 'Practice started'
      : fullDeck?.name || fullDeck?.title || 'Practice')
    : fullDeck?.title || (typeof title === 'string' ? title : undefined);
  const sectionLabel = currentCard?.title || null;

  const showByte =
    studyMode === 'lineByLine' && !disableAiFeatures && !isNotesOpen && !showGlossary;
  const showUpgradeStrip =
    studyMode === 'lineByLine' && disableAiFeatures && !isNotesOpen && !showGlossary;

  const displayLang = getDisplayLanguage(currentCard);

  // Template follow-up prompts only — generate/regenerate live in the
  // insights body (empty CTA + footer button) to avoid duplicate CTAs.
  const chatChips = [
    'How it works?',
    'Edge cases?',
    'When to use?',
    'Why this approach?'
  ];

  // Showing the full snippet is a read-only preview — disable Check
  // there because there's no per-line attempt to evaluate.
  const submitDisabled =
    isNotesOpen ||
    showGlossary ||
    (studyMode === 'lineByLine' && showFullSnippet);
  const submitLabel = studyMode === 'lineByLine' ? 'Check Line' : 'Check';

  const roadmapLanguageKey = normalizeRoadmapLanguageKey(
    fullDeck?.language || roadmapTrackMeta?.language || displayLang || ''
  );
  const roadmapLanguageLabel = roadmapLanguageKey
    ? formatLanguageDisplay(roadmapLanguageKey)
    : '';
  const roadmapTrackTitle = roadmapTrackMeta?.title
    ? formatRoadmapTrackTitle(roadmapLanguageKey, roadmapTrackMeta.title, {
      language: roadmapTrackMeta.language || roadmapLanguageKey
    })
    : '';
  const roadmapTopicTitle = fullDeck?.practiceStartedSession
    ? formatTrackTitle(currentCard?.roadmapNodeTitle || 'Practice started')
    : formatTrackTitle(fullDeck?.roadmapNodeTitle || '');
  const roadmapNodeNavTitle = roadmapNodeNav.current?.title
    ? formatTrackTitle(roadmapNodeNav.current.title)
    : roadmapTopicTitle;

  return (
    <div
      className={[
        'study-viewer',
        isExpanded ? 'expanded' : '',
        isRoadmapStudySession ? 'study-viewer--roadmap' : 'study-viewer--cookbook'
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* ─── Test mode summary overlay (unchanged) ───────────────────── */}
      {testCompleteSummary && (
        <div
          className="study-test-complete-summary-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="study-test-complete-summary-title"
        >
          <div className="study-test-complete-summary">
            <h2
              id="study-test-complete-summary-title"
              className="study-test-complete-summary-title"
            >
              {testCompleteSummary.passed ? 'Node completed!' : 'Test complete'}
            </h2>
            <p className="study-test-complete-summary-score">
              You got <strong>{testCompleteSummary.correctCount}</strong> of{' '}
              <strong>{testCompleteSummary.totalCount}</strong> correct
              ({testCompleteSummary.totalCount > 0
                ? Math.round(
                  (testCompleteSummary.correctCount / testCompleteSummary.totalCount) * 100
                )
                : 0}
              %).
            </p>
            <p className="study-test-complete-summary-message">
              {testCompleteSummary.passed
                ? 'Great work. You can continue from the roadmap.'
                : "Try again from the roadmap when you're ready."}
            </p>
            <button
              type="button"
              className="study-test-complete-summary-btn"
              onClick={dismissTestCompleteAndGoBack}
            >
              Back to roadmap
            </button>
          </div>
        </div>
      )}

      {/* ─── Main shell (+ roadmap context rail when studying a track snippet) ─ */}
      <div className="study-viewer__layout">
        {isRoadmapStudySession && (
          <StudyRoadmapContextPanel
            languageKey={roadmapLanguageKey}
            languageLabel={roadmapLanguageLabel}
            trackTitle={roadmapTrackTitle}
            topicTitle={roadmapTopicTitle}
            trackOptions={roadmapTrackOptions}
            currentTrackId={roadmapTrackId}
            onTrackSelect={handleSelectRoadmapTrack}
            onTrackLockedSelect={handleSubscriptionRequired}
            topicOptions={roadmapTopicOptions}
            currentTopicId={currentRoadmapNodeId}
            onTopicSelect={handleSelectRoadmapTopic}
            tracksLoading={roadmapLanguageTracksLoading}
            topicsLoading={roadmapNodeNavLoading && roadmapTrackNodes.length === 0}
            trackProgressCompleted={roadmapTrackProgress?.completed ?? null}
            trackProgressTotal={roadmapTrackProgress?.total ?? null}
            loading={roadmapTrackMetaLoading}
            onViewTrack={handleViewRoadmapTrack}
            showNodeNav={roadmapTrackNodes.length > 1}
            nodeNavTitle={roadmapNodeNavTitle}
            canNavigatePrev={Boolean(roadmapNodeNav.prev)}
            canNavigateNext={Boolean(roadmapNodeNav.next)}
            nodeNavLoading={roadmapNodeNavLoading}
            onNavigatePrev={
              roadmapNodeNav.prev
                ? () => handleNavigateTrackNode(roadmapNodeNav.prev)
                : undefined
            }
            onNavigateNext={
              roadmapNodeNav.next
                ? () => handleNavigateTrackNode(roadmapNodeNav.next)
                : undefined
            }
          />
        )}
        <StudyShell
          snippetIcon={
            <IconRenderer
              name={displayLang}
              type="language"
              className="study-shell__navbar-snippet-icon-svg"
            />
          }
          cookbookName={cookbookName}
          sectionName={sectionLabel}
          streak={navStreak}
          total={cards.length}
          currentIndex={currentCardIndex}
          cards={cards}
          sessionCardStatuses={sessionCardStatuses}
          onProgressDotClick={handleProgressDotClick}
          onPrev={handlePrevious}
          onNext={handleProgressNext}
          onOpenCookbookIndex={handleOpenCookbookIndex}
          cookbookIndexOpen={showGlossary}
          prevDisabled={currentCardIndex === 0 || isTestMode}
          nextDisabled={
            isTestMode
              ? currentCardIndex === cards.length - 1
              : solutionRevealed
                ? progressNextAfterRevealDisabled
                : currentCardIndex === cards.length - 1
          }
          nextHighlighted={progressNextAfterRevealHighlighted}
          showProgressDots={!isPracticeSession}
          practiceNavMode={isPracticeSession ? practiceNavMode : null}
          onPracticeNavModeChange={
            isPracticeSession ? handlePracticeNavModeChange : undefined
          }
          nextLabel={
            solutionRevealed && !progressNextAfterRevealDisabled
              ? progressNextAfterRevealLabel
              : 'Next'
          }
          lastResult={lastResult}
          cardHeader={
            <div className="study-card__header">
              <CardHeaderActions
                studyMode={studyMode}
                sessionMode={sessionMode}
                onModeChange={handleModeChange}
                onSessionModeChange={handleSessionModeChange}
                modeToggleDisabled={isTestMode}
                showNotes={isNotesOpen}
                hasNotes={Boolean(notes?.trim())}
                onToggleNotes={handleToggleNotes}
                variablesAvailable={resolvedVariableHints.length > 0}
                variablesVisible={showVariableHints}
                variablesCount={resolvedVariableHints.length}
                onToggleVariables={handleToggleVariableHints}
                showGlossary={showGlossary}
                onToggleGlossary={handleToggleGlossary}
                hintsAvailable={
                  commentHints.length > 0 && studyMode === 'lineByLine'
                }
                hintsVisible={showHints}
                hintsCount={commentHints.length}
                onToggleHints={handleToggleHints}
                isExpanded={isExpanded}
                onToggleExpand={handleExpandCard}
                onAgain={handleAgain}
                onTryAgain={handleTryAgain}
                againDisabled={isTestMode || isNotesOpen}
                againRevealed={solutionRevealed}
                onCopy={handleCopySolution}
                onSubmit={handleStudySubmit}
                submitLabel={submitLabel}
                submitDisabled={submitDisabled}
                onHover={(label) => setHoveredButtonLabel(label)}
                onLeave={() => setHoveredButtonLabel(null)}
                availability={{
                  again: !isTestMode,
                  modeToggle: !isTestMode
                }}
              />

              <div
                className={[
                  'study-card__header-meta',
                  hasMultipleDescriptionPages && 'study-card__header-meta--clickable',
                  hasMultipleDescriptionPages && 'study-card__header-meta--paginated'
                ]
                  .filter(Boolean)
                  .join(' ')}
                role={hasMultipleDescriptionPages ? 'group' : undefined}
                tabIndex={hasMultipleDescriptionPages ? 0 : undefined}
                aria-label={
                  hasMultipleDescriptionPages
                    ? `Description, page ${clampedDescriptionPageIndex + 1} of ${descriptionPages.length}. Click the left side for previous, right side for next.`
                    : undefined
                }
                title={
                  hasMultipleDescriptionPages
                    ? 'Click left for previous · click right for next'
                    : undefined
                }
                onClick={
                  hasMultipleDescriptionPages ? handleDescriptionPageClick : undefined
                }
                onKeyDown={
                  hasMultipleDescriptionPages ? handleDescriptionPageKeyDown : undefined
                }
                onMouseMove={
                  hasMultipleDescriptionPages ? handleDescriptionMetaMouseMove : undefined
                }
                onMouseLeave={
                  hasMultipleDescriptionPages
                    ? (e) => {
                      e.currentTarget.style.cursor = '';
                    }
                    : undefined
                }
              >
                <div className="study-card__subtitle">
                  <span className="study-card__subtitle-text">{descriptionPageText}</span>
                  {hasMultipleDescriptionPages && (
                    <span className="study-card__header-meta-page-count" aria-hidden="true">
                      {clampedDescriptionPageIndex + 1}/{descriptionPages.length}
                    </span>
                  )}
                </div>
              </div>
            </div>
          }
        >
          <div className="study-card">
            {/* ── Inline status / hover label / feedback strip ─────── */}
            <div className="study-card__status-strip" aria-live="polite">
              <span className="study-card__status-label">
                {hoveredButtonLabel || (
                  isTestMode
                    ? 'Test: type your answer'
                    : isNotesOpen
                      ? 'Study notes'
                      : showVariableHints
                        ? 'Variable values'
                        : showGlossary
                        ? (
                          <span className="study-card__status-label-with-icon">
                            <CookbookIndexIcon
                              className="study-card__status-label-icon"
                              aria-hidden
                              focusable="false"
                            />
                            Cookbook index
                          </span>
                        )
                        : studyMode === 'lineByLine'
                          ? showFullSnippet
                            ? `📄 Full snippet · ${lines?.length || 1} lines · practice only`
                            : `📚 Line ${Math.min((currentLineIndex || 0) + 1, lines?.length || 1)} of ${lines?.length || 1} · practice only`
                          : '✏️ Type your solution'
                )}
              </span>
              {studyMode === 'lineByLine' &&
                !isNotesOpen &&
                !showGlossary &&
                (lines?.length || 0) > 0 && (
                  <button
                    type="button"
                    className={[
                      'study-card__view-toggle',
                      showFullSnippet && 'study-card__view-toggle--active'
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => setShowFullSnippet((v) => !v)}
                    title={
                      showFullSnippet
                        ? 'Return to the current line'
                        : 'Preview the full snippet (read-only)'
                    }
                    aria-pressed={showFullSnippet}
                  >
                    {showFullSnippet ? '◂ Back to line' : 'View full snippet ▸'}
                  </button>
                )}
              {feedback && (
                <div
                  className={[
                    'study-card__feedback',
                    solutionRevealed
                      ? 'study-card__feedback--revealed'
                      : isCorrect
                        ? 'study-card__feedback--correct'
                        : 'study-card__feedback--wrong'
                  ].join(' ')}
                  role="status"
                >
                  <span className="study-card__feedback-text">{feedback}</span>
                  <button
                    type="button"
                    className="study-card__feedback-close"
                    onClick={() => setFeedback('')}
                    aria-label="Dismiss feedback"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>

            {/* ── Body: split (Learn + AI) or single column ─────────── */}
            <div
              className={[
                'study-card__body',
                showByte && 'study-card__body--split'
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {/* Left: code panel (or notes / glossary takeover) ───── */}
              <div className="study-card__code-panel">
                {showGlossary ? (
                  <div className="study-card__glossary">
                    {isTestMode && (
                      <p className="study-card__glossary-test-hint" aria-live="polite">
                        In test mode you can only go to snippets you haven&apos;t answered yet.
                      </p>
                    )}
                    <div className="study-card__glossary-list">
                      {cards.map((card, index) => {
                        const isAnsweredInTest =
                          isTestMode &&
                          Array.isArray(testModeSnippetResults) &&
                          testModeSnippetResults[index] != null;
                        const cardProgress = card.progress ?? null;
                        const reviewLabel = formatCookbookIndexReviewLabel(cardProgress);
                        const isReviewDue = isCookbookSnippetDueToday(cardProgress);
                        const effectiveDue = resolveSrsNextReviewDate(cardProgress);
                        return (
                          <button
                            key={card.id}
                            type="button"
                            className={[
                              'study-card__glossary-item',
                              index === currentCardIndex &&
                              'study-card__glossary-item--active',
                              isAnsweredInTest &&
                              'study-card__glossary-item--test-answered'
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            onClick={() => {
                              if (isAnsweredInTest) return;
                              navigateToCard(card.id);
                              setShowGlossary(false);
                              resetState();
                            }}
                            title={
                              isAnsweredInTest
                                ? `${card.title || 'Untitled'} (already answered)`
                                : `${card.title || 'Untitled'} (Card ${index + 1})`
                            }
                            disabled={isAnsweredInTest}
                          >
                            <div className="study-card__glossary-num">{index + 1}</div>
                            <div className="study-card__glossary-content">
                              <div className="study-card__glossary-title">
                                {card.title || 'Untitled'}
                              </div>
                              {card.description && (
                                <div className="study-card__glossary-desc">
                                  {card.description}
                                </div>
                              )}
                            </div>
                            <span
                              className={[
                                'study-card__glossary-review',
                                isReviewDue && 'study-card__glossary-review--due'
                              ]
                                .filter(Boolean)
                                .join(' ')}
                              title={
                                !isCookbookSnippetStarted(cardProgress)
                                  ? 'Not yet reviewed'
                                  : effectiveDue
                                    ? `Next review: ${effectiveDue.toLocaleString()}`
                                    : 'Due today'
                              }
                            >
                              {reviewLabel}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : isNotesOpen ? (
                  <div className="study-card__notes-panel">
                    <header className="study-card__notes-panel-header">
                      <div className="study-card__notes-panel-toolbar">
                        <div className="study-card__notes-panel-tabs">
                          <button
                            type="button"
                            className={[
                              'study-card__notes-panel-tab',
                              notesViewMode === 'notes' &&
                              'study-card__notes-panel-tab--active'
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            onClick={() => setNotesViewMode('notes')}
                          >
                            My notes
                          </button>
                          <button
                            type="button"
                            className={[
                              'study-card__notes-panel-tab',
                              notesViewMode === 'description' &&
                              'study-card__notes-panel-tab--active'
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            onClick={() => setNotesViewMode('description')}
                          >
                            Description
                          </button>
                        </div>
                        <div className="study-card__notes-panel-status">
                          {isSavingNotes ? 'Saving…' : 'Saved'}
                        </div>
                      </div>
                    </header>
                    <div className="study-card__notes-panel-body">
                      <div className="study-card__notes-sheet">
                        {notesViewMode === 'notes' ? (
                          <textarea
                            ref={textareaRef}
                            value={notes}
                            onChange={(e) => handleNotesChange(e.target.value)}
                            placeholder="Jot down reminders, gotchas, or how you solved this…"
                            className="study-card__notes-sheet-input"
                            rows={14}
                            aria-label="Study notes for this snippet"
                          />
                        ) : (
                          <div className="study-card__notes-sheet-description">
                            {currentCard?.description ||
                              'No description available for this snippet.'}
                          </div>
                        )}
                      </div>
                    </div>
                    <footer className="study-card__notes-panel-footer">
                      Notes are personal and saved to this snippet only — they never affect grading.
                    </footer>
                  </div>
                ) : showVariableHints ? (
                  <div
                    className="study-card__variables-panel"
                    aria-label="Variable values"
                  >
                    <header className="study-card__variables-panel-header">
                      Variable values
                    </header>
                    <div className="study-card__variables-panel-body">
                      <div className="study-card__variables-panel-list">
                        {visibleVariableHints.length > 0 ? (
                          visibleVariableHints.map((hint) => (
                            <div
                              key={`${hint.name}-${hint.lineIndex}`}
                              className="study-card__variables-panel-item"
                            >
                              <code className="study-card__variables-panel-name">
                                {hint.name}
                              </code>
                              <span
                                className="study-card__variables-panel-separator"
                                aria-hidden
                              >
                                →
                              </span>
                              <code className="study-card__variables-panel-value">
                                {hint.value}
                              </code>
                            </div>
                          ))
                        ) : (
                          <div className="study-card__variables-panel-empty">
                            {studyMode === 'lineByLine'
                              ? 'No variables declared yet on previous lines.'
                              : 'No variables found in this snippet.'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Line-comparison panel (line-by-line wrong answer) */}
                    {studyMode === 'lineByLine' && !showFullSnippet && lineComparison && showLine && (
                      <div className="study-card__line-comparison">
                        <div className="study-card__line-comparison-row">
                          <div className="study-card__line-comparison-col">
                            <div className="study-card__line-comparison-label">
                              Your code
                            </div>
                            <pre className="study-card__line-comparison-code study-card__line-comparison-code--user">
                              {lineComparison.userText || '(empty)'}
                            </pre>
                          </div>
                          <div className="study-card__line-comparison-col">
                            <div className="study-card__line-comparison-label">
                              Solution
                            </div>
                            <pre className="study-card__line-comparison-code study-card__line-comparison-code--solution">
                              {lineComparison.solutionText || '(empty)'}
                            </pre>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="study-card__editor-surface">
                      {studyMode === 'regular' ? (
                        <StudyModeCodeEditor
                          key={currentCard?.id || `study-card-${currentCardIndex}`}
                          value={userCode}
                          onChange={(value) => {
                            handleEditorChange(value || '');
                          }}
                          language={extractLanguageFromContent(currentCard)}
                          placeholder="Enter your code here..."
                          readOnly={false}
                          autoFocus
                        />
                      ) : showFullSnippet ? (
                        <StudyModeCodeEditor
                          value={
                            /*
                             * Prefer the raw `currentCard.solution`: the
                             * `lines` array used by line-by-line mode
                             * is trimmed so the user types each line
                             * without worrying about leading indents,
                             * which means joining it back loses the
                             * code's original formatting.
                             */
                            currentCard?.solution ||
                            (Array.isArray(lines) ? lines.join('\n') : '')
                          }
                          onChange={() => { }}
                          language={extractLanguageFromContent(currentCard)}
                          placeholder=""
                          readOnly
                        />
                      ) : (
                        <textarea
                          ref={textareaRef}
                          value={userCode}
                          onChange={(e) => {
                            handleEditorChangeLineByLine(e.target.value);
                            if (lineComparison) setLineComparison(null);
                          }}
                          onFocus={() => {
                            if (
                              !hasStartedCoding &&
                              userCode === lines[currentLineIndex]
                            ) {
                              setUserCode('');
                              setHasStartedCoding(true);
                              setLineComparison(null);
                            }
                          }}
                          placeholder={
                            hasStartedCoding
                              ? `Enter line ${currentLineIndex + 1} of ${lines.length}...`
                              : (lines[currentLineIndex] ||
                                `Enter line ${currentLineIndex + 1} of ${lines.length}...`)
                          }
                          className={[
                            'study-card__line-input',
                            lineComparison && 'study-card__line-input--with-comparison'
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          rows={10}
                        />
                      )}
                    </div>

                    {/* Hints panel for line-by-line mode */}
                    {commentHints.length > 0 && studyMode === 'lineByLine' && showHints && !showFullSnippet && (
                      <aside
                        className="study-card__hints-panel"
                        aria-label="Comment hints"
                      >
                        <div className="study-card__hints-panel-header">Hints</div>
                        <div className="study-card__hints-panel-list">
                          {(() => {
                            const activeHint = commentHints.find(
                              (h) =>
                                currentLineIndex >= h.startLineIndex &&
                                currentLineIndex < h.startLineIndex + h.linesAfter
                            );
                            if (activeHint) {
                              const activeBlockIndex = commentHints.indexOf(activeHint);
                              const blockLabel = commentHints.length > 1
                                ? `Block ${activeBlockIndex + 1} of ${commentHints.length}`
                                : null;
                              return (
                                <div className="study-card__hint-item study-card__hint-item--active">
                                  {blockLabel && (
                                    <div className="study-card__hint-block-context">
                                      {blockLabel}
                                    </div>
                                  )}
                                  <div className="study-card__hint-text">
                                    {activeHint.text}
                                  </div>
                                </div>
                              );
                            }
                            return (
                              <div className="study-card__hint-empty">
                                No hint for this line.
                              </div>
                            );
                          })()}
                        </div>
                      </aside>
                    )}
                  </>
                )}
              </div>

              {/* Right: Byte coaching companion (Learn Mode only) ───── */}
              {showByte && (
                <ByteCompanion
                  statusLabel={`${displayLang} · ${currentCard?.title || 'Snippet'}`}
                  explanation={activeInsight}
                  isLoadingInsights={isInsightLoading}
                  insightsError={false}
                  onGenerateInsights={handleGenerateInsights}
                  onRegenerateInsights={handleRegenerateInsights}
                  generateDisabled={isExplaining}
                  chatChips={chatChips}
                  onAsk={handleAskByte}
                  chatDisabled={isExplaining}
                  key={`byte-${currentCard?.id}-${insightVersion}`}
                />
              )}
            </div>

            {/* Free-tier upgrade strip — between body and footer */}
            {showUpgradeStrip && (
              <ByteUpgradeStrip
                message={
                  disableAiFeatures
                    ? 'Sign up for AI-powered explanations and follow-up chat with Byte'
                    : 'Get AI-powered explanations and follow-up chat with Byte'
                }
                ctaLabel={disableAiFeatures ? 'Sign up' : 'Upgrade'}
                onUpgrade={handleUpgradeClick}
              />
            )}
          </div>
        </StudyShell>
      </div>

      <StudyExperiencePopup
        isOpen={showExperiencePopup}
        panel={experiencePanel}
        onPanelChange={setExperiencePanel}
        experienceData={experienceData}
        sessionSummary={sessionSummaryPayload}
        studyMode={studyMode}
        displayStreak={displayStreak}
        streakAtRisk={streakAtRisk}
        completedToday={completedToday}
        masteredToday={masteredToday}
        roleTitle={sessionHasRoleExp ? primaryRoleMeta?.title || null : null}
        showSessionTab={showSessionSummary}
        showContinuePath={showSessionSummary && sessionHasRoleExp && Boolean(primaryRoleId)}
        continuePathLabel={continuePathLabel}
        onContinuePath={handleContinueRolePath}
        onDismiss={handleExperiencePopupDismiss}
      />
    </div>
  );
};

export default Study;
